import { makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import CommentViewModel from './CommentViewModel';
import {AnnouncementComment} from 'main/stores/announcements';
import {types, endpoints, auth} from 'main/tools';
import {MAXIMUM_ANNOUNCEMENT_HEIGHT} from './constants';
import _ from 'lodash';

class AnnouncementState {
  store = new DomainStore();

  showComments = false;
  creatingCommentViewModel = new CommentViewModel(new AnnouncementComment(), this);
  emojiSelectorOpen = false;
  modifyingReaction = false;
  commenting = false;
  editCommentModalOpen = false;
  deleteCommentModalOpen = false;
  editingCommentViewModel;
  announcementVisibilityModalOpen = false;
  openEditAnnouncementModal;
  announcement;
  expanded = false;
  height = 0;
  errors = {};
  descriptionElement;

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({announcement, openEditAnnouncementModal, openDeleteAnnouncementModal, pinAnnouncement, unpinAnnouncement}) {
    this.announcement = announcement;
    this.openEditAnnouncementModal = openEditAnnouncementModal;
    this.openDeleteAnnouncementModal = openDeleteAnnouncementModal;
    this.pinAnnouncement = pinAnnouncement;
    this.unpinAnnouncement = unpinAnnouncement;
  }

  async addReaction(emojiKey) {
    if (!this.doesReactionExist(emojiKey)) {
      if (this.modifyingReaction) return null;

      this.modifyingReaction = true;
      await this.store.post(
        endpoints.ANNOUNCEMENT_REACTIONS,
        types.ANNOUNCEMENT_REACTION,
        {
          announcementId: this.announcement.id,
          emojiKey: emojiKey
        }
      );
      await this.reloadAnnouncement();
      this.modifyingReaction = false;
    }

    this.closeEmojiPicker();
  }

  async deleteReaction(reaction) {
    if (this.modifyingReaction) return null;

    this.modifyingReaction = true;
    await this.store.destroy(reaction);
    await this.reloadAnnouncement();
    this.modifyingReaction = false;
  }

  async reloadAnnouncement() {
    await this.store._compose([
      endpoints.ANNOUNCEMENT.with(this.announcement.id)
    ]);

    const announcement = this.store._getSingle(types.ANNOUNCEMENT, {id: this.announcement.id});

    this.announcement.update(announcement);
  }

  toggleReaction(reaction) {
    if (reaction.userReaction) {
      this.deleteReaction(reaction.userReaction);
    } else {
      this.addReaction(reaction.emojiKey);
    }
  }

  expandComments() {
    this.showComments = !this.showComments;
  }

  async submitComment(commentViewModel) {
    this.commenting = true;

    const {model, errors} = await this.store.post(
      endpoints.ANNOUNCEMENT_COMMENTS,
      types.ANNOUNCEMENT_COMMENT,
      {
        announcementId: this.announcement.id,
        content: commentViewModel.editingComment
      }
    );

    this.errors = errors;

    if (model) {
      await this.reloadAnnouncement();
      commentViewModel.editingComment = '';
    }

    this.commenting = false;
  }

  editCommentClicked(comment) {
    this.setCurrentComment(comment);
    this.openCommentEditModal();
  }

  deleteCommentClicked(comment) {
    this.setCurrentComment(comment);
    this.openCommentDeleteModal();
  }

  openCommentEditModal() {
    this.editCommentModalOpen = true;
    this.errors = {};
  }

  openAnnouncementVisibilityModal() {
    this.announcementVisibilityModalOpen = true;
  }

  closeAnnouncementVisibilityModal() {
    this.announcementVisibilityModalOpen = false;
  }

  openCommentDeleteModal() {
    this.deleteCommentModalOpen = true;
  }

  closeCommentEditModal() {
    this.editCommentModalOpen = false;
    this.setCurrentComment(null);
  }

  closeCommentDeleteModal() {
    this.deleteCommentModalOpen = false;
    this.setCurrentComment(null);
  }

  setCurrentComment(comment) {
    this.editingCommentViewModel = comment;
  }

  confirmCommentDelete() {
    this.deleteComment();
    this.closeCommentDeleteModal();
  }

  confirmCommentEdit(commentViewModel) {
    this.editComment(commentViewModel);
    this.closeCommentEditModal();
  }

  async deleteComment() {
    await this.store.destroy(this.editingCommentViewModel.data);
    await this.reloadAnnouncement();
  }

  async editComment(commentViewModel) {
    await this.store.patch(
      endpoints.ANNOUNCEMENT_COMMENT.with(commentViewModel.data.id),
      types.ANNOUNCEMENT_COMMENT,
      commentViewModel.data
    );
    await this.reloadAnnouncement();
  }

  toggleEmojiList() {
    this.emojiSelectorOpen = !this.emojiSelectorOpen;
  }

  closeEmojiPicker() {
    this.emojiSelectorOpen = false;
  }

  async submit() {
    await this.submitComment(this);
  }

  get commentViewModels() {
    return this.showComments ? this.allCommentViewModels : this.latestCommentViewModels;
  }

  get allCommentViewModels() {
    return this.announcement.comments.map(comment => new CommentViewModel(comment, this));
  }

  get latestCommentViewModels() {
    const latestComments = _.takeRight(this.announcement.comments, 2);

    return latestComments.map(comment => new CommentViewModel(comment, this, this));
  }

  get customLinks() {
    const links = [];

    if (this.announcement.hasLink('pin')) {
      links.push({
        order: 20,
        text: 'hr_dashboard.feed.announcements.Pin',
        action: (announcement) => this.pinAnnouncement(announcement)
      });
    }

    if (this.announcement.hasLink('unpin')) {
      links.push({
        order: 20,
        text: 'hr_dashboard.feed.announcements.Unpin',
        action: (announcement) => this.unpinAnnouncement(announcement)
      });
    }

    return links;
  }

  get expandCommentsText() {
    return this.showComments ? 'hr_dashboard.feed.announcements.Hide older comments' : 'hr_dashboard.feed.announcements.View previous comments';
  }

  get showExpandCommentsAction() {
    return this.announcement.comments.length > 2;
  }

  doesReactionExist(emoji) {
    return this.announcement.reactions.some(reaction =>
      reaction.user.id === auth.user.id && emoji === reaction.emojiKey
    );
  }

  expandDescription() {
    this.expanded = true;
  }

  get expandable() {
    return this.height > MAXIMUM_ANNOUNCEMENT_HEIGHT;
  }

  mount() {
    this.descriptionElement.querySelectorAll('img').forEach(img => {
      this.recalculateHeightAfterImageLoad(img);
    });
    this.height = this.descriptionElement.clientHeight;
  }

  recalculateHeightAfterImageLoad(img, retryCounter = 0) {
    if (img.complete) {
      this.height = this.descriptionElement.clientHeight;
    } else if (retryCounter < 50) {
      setTimeout(() => this.recalculateHeightAfterImageLoad(img, retryCounter + 1), 200);
    }
  }

  setRef(element) {
    this.descriptionElement = element;
  }
}

export default AnnouncementState;
